import customFetch, { fetchDelete, fetchPost, fetchPut } from '@/lib/customFetch';
import useLocalStorage from '@/lib/use-local-storage.tsx';
import {
  keepPreviousData,
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
  UndefinedInitialDataOptions,
  DefaultError,
  UseMutationOptions
} from '@tanstack/react-query';

/**
 * Create a simple QueryClient without global error handlers
 * to avoid duplicate toast notifications
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});

/**
 * Use in the root
 */
export function QueryContext({ children }: { children: JSX.Element }) {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

/**
 * QueryData function that uses customFetch
 * Note: Error handling is done by customFetch internally and by components that use this function
 */
export function QueryData<TResponse extends object>(
  options: UndefinedInitialDataOptions<TResponse> & { url: string, queryParams?: { [key: string]: string | number | number[] | boolean | Date }, initialData?: any }
) {
  const [filters] = useLocalStorage<any[]>('filters');
  options.queryKey = [...(filters || []), ...options.queryKey];
  options.refetchOnMount = options.refetchOnMount ?? 'always';
  options.refetchOnWindowFocus =  options.refetchOnWindowFocus ?? false;
  options.retry = options.retry ?? 1;
  options.queryFn = options.queryFn || (async () => {
    return await customFetch<TResponse>(
      appendQueryString(options.url, options.queryParams),
      {},
    );
  });

  return useQuery(options);
}

/**
 *
 * @param url path, it will be appended to `BASE_URL`
 * @param queryKey set an **unique** name for request, it manages query `caching` for you based on query `keys`
 * @param page it will be appended as query string `required param`
 * @param pageSize it will be appended as query string `required param`
 * @param queryParams aditional query string params
 * @param isEnabled Set this to `false` to disable automatic refetching when the query mounts or changes query keys. To refetch the query, use the `refetch method` returned. Defaults to `true`.
 * @param staleTime The time in milliseconds after data is considered stale. If set to `Infinity`, the data will never be considered stale.
 * @returns
 */
export function QueryDataPaged<TResponse extends object>(
  url: string,
  queryKey: unknown[],
  page: number,
  pageSize: number = 10,
  queryParams?: QueryStringParams,
  isEnabled?: boolean,
  staleTime?: number,
) {
  const URI = appendQueryString(url, {
    Page: page,
    PageSize: pageSize,
    ...queryParams,
  });
  return useQuery({
    queryKey: [...queryKey, page, pageSize],
    queryFn: async () => {
      const response = await customFetch<TResponse>(URI, {});
      return response as TResponse;
    },
    placeholderData: keepPreviousData,
    staleTime: staleTime ?? 5000,
    enabled: isEnabled,
  });
}

export type MutationFnProps<TRequest> = { data?: TRequest; queryString?: QueryStringParams };

/**
 *
 * @param url  path, it will be appended to `BASE_URL`
 * @param queryKey it manages query `caching` for you based on query `keys`. __Hint: Use the same key used when getting data__
 * @param successFn triggered on success, can be used to handle toasts or redirects, for example.
 * @returns UseMutationResult
 ```ts
 {status:string ,error:unknown, mutate:(MutationFnProps) => void};

 MutationFnProps<TRequest> = { data?: TRequest; queryString?: QueryStringParams };
 ```
 */
export function UpdateData<TRequest extends object = object, TResponse extends object = object>(
  { url, queryKey, successFn}: {url: string, queryKey: unknown[], successFn?: () => void}
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ data, queryString }: MutationFnProps<TRequest>) => {
      const response = await fetchPut<TResponse, TRequest>(
        appendQueryString(url, queryString),
        data,
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      if (successFn) {
        successFn();
      }
    },
  });
}
/**
 *
 * @param url  path, it will be appended to `BASE_URL`
 * @param queryKey it manages query `caching` for you based on query `keys`. __Hint: Use the same key used when getting data__
 * @returns UseMutationResult
 ```ts
 {status:string ,error:unknown, mutate:(MutationFnProps) => void};

 MutationFnProps<TRequest> = { data?: TRequest; queryString?: QueryStringParams };
 ```
 */
export function PostData<TRequest extends object = object, TResponse extends object = object, TError = DefaultError, TVariables = unknown, TContext = unknown>(
  options: UseMutationOptions<TResponse, TError, TVariables, TContext> & {
    url: string,
    queryKey: string[],
    successFn?: () => void
  }
) {

  const queryClient = useQueryClient();
  const mutationFn = options.mutationFn = options.mutationFn || (async ({ data, queryString }: MutationFnProps<TRequest>) => await fetchPost<TResponse, TRequest>(
    { url: appendQueryString(options.url, queryString), body: data },
  ));

  const onSuccess = options.onSuccess = options.onSuccess || (() => {
    queryClient.invalidateQueries({ queryKey: options.queryKey });
    if (options.successFn) {
      options.successFn();
    }
  });

  return useMutation<TResponse, TError, TVariables, TContext>({
    ...options,
    mutationFn,
    onSuccess,
  });
}

/**
 *
 * @param url  path, it will be appended to `BASE_URL`
 * @param queryKey it manages query `caching` for you based on query `keys`. __Hint: Use the same key used when getting data__
 * @param successFn triggered on success, can be used to handle toasts or redirects, for example.
 * @returns UseMutationResult
 ```ts
 {status:string ,error:unknown, mutate:(MutationFnProps) => void};

 MutationFnProps<TRequest> = { data?: TRequest; queryString?: QueryStringParams };
 ```
 */
export function DeleteData<TRequest extends object = object, TResponse extends object = object>(
  {
    url, queryKey, successFn = () => {}
  }: { url: string, queryKey: string[], successFn?: () => void },
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ data, queryString }: MutationFnProps<TRequest>) => {
      const response = await fetchDelete<TResponse, TRequest>(
        appendQueryString(url, queryString),
        data,
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKey });
      successFn();
    },
  });
}

export function InvalidadeCachedValues(queryKey: unknown[]) {
  const queryClient = useQueryClient();
  queryClient.invalidateQueries({ queryKey: queryKey });
}

export type QueryStringParams = { [key: string]: string | number | number[] | boolean | Date };
const appendQueryString = (
  uri: string,
  params?: { [key: string]: string | number | number[] | boolean | Date },
) => {
  if (!params || Object.keys(params).length == 0) return uri;

  const queryString: string[] = [];
  for (const key in params) {
    if (Array.isArray(params[key])) {
      const elements: any = params[key];
      elements.forEach((element) => {
        queryString.push(`${key}=${element}`);
      });
    } else {
      queryString.push(`${key}=${params[key]}`);
    }
  }
  if (!uri.includes('?')) {
    uri = `${uri}?${queryString.join('&')}`;
  } else {
    uri = `${uri}&${queryString.join('&')}`;
  }
  return uri;
};
